import { IFieldDef, IFieldInfoProperties, IGetValueArgs, TValidatorFnResult } from "@components/smart/FieldInfo";
import {
    CompanyBankAccountEntity,
    EntityTypeName,
    INumberRangeDefinitionEntity,
    INumberRangeEntity,
    NumberRangeDefinitionEntity,
    CashBoxEntity,
    LoanAccountEntity
} from "@odata/GeneratedEntityTypes";
import { IFormatOptions } from "@odata/OData.utils";
import i18next from "i18next";
import { ValidationError } from "yup";

import { BasicInputSizes, FieldType, NavigationSource, ValidatorType } from "../../enums";
import { TValue } from "../../global.types";

export const NumberRangeDefinitionPatternFormatter = (val: TValue, args?: IFormatOptions): string => {
    const item = args.item as INumberRangeDefinitionEntity;
    const parts = [
        item?.NumberRangeTypePrefix,
        item?.CarryForwardPrefix,
        item?.Prefix,
        "x".repeat(item?.NumberOfDigits),
        item?.Suffix
    ].filter(part => !!part);
    return parts?.length > 0 ? parts.join(" ") : undefined;
};

const NumberRangeToNumberRangeDefinitionFormatter = (val: TValue, args?: IFormatOptions) => {
    const entity = (args.bindingContext && args.storage ? args.storage.getValue(args.bindingContext) ?? args.entity : args.entity) as INumberRangeEntity;

    return NumberRangeDefinitionPatternFormatter(val, {
        ...args,
        item: entity?.Definition
    });
};

export const getDefaultRangeSelectAdditionalProperties = (prefix = ""): IFieldDef[] => {
    return [
        { id: `${prefix}Definition/Suffix` },
        { id: `${prefix}Definition/NumberRangeTypePrefix` },
        { id: `${prefix}Definition/Prefix` },
        { id: `${prefix}Definition/NumberOfDigits` },
        { id: `${prefix}Definition/IsDefault` }
    ];
};

const additionDefFields = [
    { id: NumberRangeDefinitionEntity.Suffix },
    { id: NumberRangeDefinitionEntity.NumberRangeTypePrefix },
    { id: NumberRangeDefinitionEntity.CarryForwardPrefix },
    { id: NumberRangeDefinitionEntity.Prefix },
    { id: NumberRangeDefinitionEntity.NumberOfDigits },
    { id: NumberRangeDefinitionEntity.IsDefault },
    { id: NumberRangeDefinitionEntity.IsActive },
    { id: NumberRangeDefinitionEntity.Name }
];

type TSupportedTypes =
    EntityTypeName.LoanStatement
    | EntityTypeName.CashReceiptIssued
    | EntityTypeName.CashReceiptReceived
    | EntityTypeName.BankStatement;
export const getDefaultRangeDefinition = (type: TSupportedTypes): IFieldInfoProperties => {
    let propName: string;

    switch (type) {
        case EntityTypeName.BankStatement:
            propName = CompanyBankAccountEntity.BankStatementNumberRangeDefinition;
            break;

        case EntityTypeName.LoanStatement:
            propName = LoanAccountEntity.BankStatementNumberRangeDefinition;
            break;

        case EntityTypeName.CashReceiptIssued:
            propName = CashBoxEntity.ReceiptIssuedNumberRangeDefinition;
            break;

        case EntityTypeName.CashReceiptReceived:
            propName = CashBoxEntity.ReceiptReceivedNumberRangeDefinition;
            break;
    }

    return {
        type: FieldType.ComboBox,
        width: BasicInputSizes.XXL,
        formatter: NumberRangeDefinitionPatternFormatter,
        isRequired: true,
        fieldSettings: {
            shouldDisplayAdditionalColumns: true,
            displayName: "Prefix",
            localDependentFields: [{
                from: { id: NumberRangeDefinitionEntity.IsActive },
                to: { id: NumberRangeDefinitionEntity.IsActive },
                navigateFrom: NavigationSource.Itself
            }, {
                from: { id: NumberRangeDefinitionEntity.CarryForwardPrefix },
                to: { id: NumberRangeDefinitionEntity.CarryForwardPrefix },
                navigateFrom: NavigationSource.Itself
            }, {
                from: { id: NumberRangeDefinitionEntity.NumberOfDigits },
                to: { id: NumberRangeDefinitionEntity.NumberOfDigits },
                navigateFrom: NavigationSource.Itself
            }, {
                from: { id: NumberRangeDefinitionEntity.Suffix },
                to: { id: NumberRangeDefinitionEntity.Suffix },
                navigateFrom: NavigationSource.Itself
            }]
        },
        filter: {
            select: `NumberRangeTypeCode eq '${type}' and IsActive eq true`
        },
        columns: [
            {
                id: "Prefix",
                formatter: NumberRangeDefinitionPatternFormatter,
                additionalProperties: additionDefFields
            },
            {
                id: "Name"
            }
        ],
        additionalProperties: additionDefFields,
        validator: {
            type: ValidatorType.Custom,
            settings: {
                customValidator: (value: TValue, args: IGetValueArgs): TValidatorFnResult => {
                    const { entity } = args.storage.data;
                    if (!entity[propName]?.IsActive) {
                        return new ValidationError(i18next.t("Error:NumberRangeIsNotActive"), value, args.bindingContext.getPath(true));
                    }
                    return true;
                }
            }
        }
    };
};


export const getDefaultRange = (type: string, preload = false): IFieldInfoProperties => {
    return {
        type: FieldType.ComboBox,
        width: BasicInputSizes.XXL,
        formatter: NumberRangeToNumberRangeDefinitionFormatter,
        isRequired: true,
        fieldSettings: {
            shouldDisplayAdditionalColumns: true,
            displayName: "Definition/Prefix",
            preloadItems: preload
        },
        filter: {
            select: `Definition/NumberRangeTypeCode eq '${type}'`
        },
        columns: [
            {
                id: "Definition/Prefix",
                formatter: NumberRangeToNumberRangeDefinitionFormatter,
                additionalProperties: getDefaultRangeSelectAdditionalProperties("/")
            },
            {
                id: "Definition/Name"
            }
        ],
        additionalProperties: getDefaultRangeSelectAdditionalProperties()
    };
};
